import React, { useState, useEffect } from "react";
import { toast } from "../../utils/appToast";
import api from "./api"; // Adjust the import path as necessary
import {
  getCachedSubscriptionStatus,
  setCachedSubscriptionStatus,
} from "../../utils/subscriptionStatus";
import "./apiKeySection.css"; // style separately

const ApiKeySection = () => {
  const [apiKey, setApiKey] = useState(null); // Initially null
  const [isLoading, setIsLoading] = useState(false);
  const [isApiKeyActive, setIsApiKeyActive] = useState(false); // Track if API key is active
  const [isRefreshing, setIsRefreshing] = useState(true); // Track if subscription status is being fetched

  // Fetch the subscription status when the component mounts
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      setIsRefreshing(true); // Start refreshing state
      try {
        const cached = getCachedSubscriptionStatus();
        let data = cached;
        if (!data) {
          const response = await api.get(
            "api/v1/accounts/get-subscription-status/",
            { validateStatus: (status) => true }
          );
          data = response.data;
          if (response.status === 200) setCachedSubscriptionStatus(data);
        }

        if (data?.api_key_active) {
          setApiKey(data.api_key); // Set the API key from the response
          setIsApiKeyActive(true); // Set the key as active
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      } finally {
        setIsRefreshing(false); // Stop refreshing state
      }
    };

    fetchSubscriptionStatus(); // Fetch on mount
  }, []); // Only run on mount

  const handleGenerate = async () => {
    console.log("Generating API key...");
    setIsLoading(true);
    try {
      const response = await api.post("api/v1/accounts/generate-api-key/", {});
      const newKey = response.data.api_key; // Get the generated key
      console.log("Generated API key:", newKey);
      setApiKey(newKey); // Set the generated key
      setIsApiKeyActive(true); // Mark the key as active
      toast.success("API Key generated successfully!");
    } catch (error) {
      console.error("Error generating API key:", error);
      toast.error("Failed to generate API Key.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    console.log("Copying API key to clipboard...");
    if (apiKey) {
      navigator.clipboard.writeText(apiKey); // Copy the API key to the clipboard
      toast.success("Copied to clipboard!");
    }
  };

  const handleRegenerate = async () => {
    console.log("Regenerating API key...");
    setIsLoading(true);
    try {
      const response = await api.post("api/v1/accounts/generate-api-key/", {});
      const regeneratedKey = response.data.api_key; // Get the regenerated key
      console.log("Regenerated API key:", regeneratedKey);
      setApiKey(regeneratedKey); // Update the API key
      toast.info("API Key regenerated.");
    } catch (error) {
      console.error("Error regenerating API key:", error);
      toast.error("Failed to regenerate API Key.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async () => {
    console.log("Revoking API key...");
    setIsLoading(true);
    try {
      const response = await api.post("api/v1/accounts/revoke-api-key/");
      console.log("Revocation response:", response.data);
      if (response.data.detail === "API key revoked successfully.") {
        setApiKey(null); // Reset the API key
        setIsApiKeyActive(false); // Mark the API key as inactive
        toast.error("API Key revoked.");
      }
    } catch (error) {
      console.error("Error revoking API key:", error);
      toast.error("Failed to revoke API Key.");
    } finally {
      setIsLoading(false);
    }
  };

  console.log("apiKey:", apiKey);
  console.log("isApiKeyActive:", isApiKeyActive);

  return (
    <div className="api-section">
      <h3 className="api-title">API</h3>

      {/* Show loading or refreshing state */}
      {isRefreshing ? (
        <div className="api-card">
  <div className="relative z-10 flex flex-col items-center justify-center">
      <div className="mb-3 animate-bounce">
        <img
          src="/docusensas-logo.svg"
          alt="DocuSensa Logo"
          className="w-[60px] h-[60px]"
          style={{ filter: "brightness(0) saturate(100%)" }}
        />
      </div>


      <p
        className="mt-2 text-[14px] font-medium text-[#52525B]"
        style={{ fontFamily: "Outfit, ui-sans-serif, system-ui" }}
      >
       Refreshing subscription status.......
      </p>
    </div>        </div>
      ) : (
        <>
          {/* First-time Visit or API Key not active */}
          {!apiKey && !isApiKeyActive ? (
            <div className="api-card">
              <div className="api-header">
                <div className="flex">
                  <img src="/key-icon.svg" alt="key" className="api-icon" />
                  <h4 className="api-subtitle">Generate Your API Key</h4>
                </div>
              </div>
              <p className="api-description">
                This key allows you to connect DocuSensa with your own systems.
              </p>
              <button
                className="btn-generate"
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? "Generating..." : "Generate API Key"}
              </button>

              <p className="api-tip">
                <img src="/bulb-icon.svg" alt="tip" className="tip-icon" />
                Tip: This key is like a password. Keep it safe and never expose it in public tools.
              </p>
            </div>
          ) : (
            <div>
              {/* API Key Displayed (either newly generated or fetched) */}
              <div className="api-card">
                <div className="api-header">
                  <div className="flex">
                    <img src="/key-icon.svg" alt="key" className="api-icon" />
                    <h4 className="api-subtitle">Your API Key</h4>
                  </div>
                </div>

                <div className="group relative">
                  {apiKey && (
                    <div className="relative">
                      <p className="api-key text-[#3F3F46] font-space-grotesk text-sm font-medium break-all max-w-full pr-10">
                        {apiKey}
                      </p>

                      <p className="text-sm text-gray-500 mt-2">
                        Click on{" "}
                        <span
                          className="text-blue-500 cursor-pointer underline"
                          onClick={handleCopy}
                        >
                          <strong>copy</strong>
                        </span>{" "}
                        to copy the token.
                      </p>
                    </div>
                  )}
                </div>

                <div className="api-actions">
                  {!isApiKeyActive && (
                    <button className="btn-copy" onClick={handleCopy}>
                      Copy
                    </button>
                  )}
                  <button className="btn-regenerate" onClick={handleRegenerate}>
                    Regenerate
                  </button>
                  <button
                    className="btn-revoke"
                    onClick={handleRevoke}
                    onMouseEnter={(e) => (e.target.style.background = "#ffebeb")}
                    onMouseLeave={(e) => (e.target.style.background = "#fff")}
                  >
                    Revoke
                  </button>
                </div>

                <p className="api-tip">
                  <img src="/bulb-icon.svg" alt="tip" className="tip-icon" />
                  Keep this key secure. You will not be able to see it again after this session.
                </p>
              </div>

              <div className="api-link">
                <p className="api-link-text">API Link</p>
                <p className="api-link-texts">Click this link to get API details</p>
                <a
                  href="https://docusensa.gitbook.io/"
                  className="api-link-url mt-2"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://docusensa.gitbook.io/
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApiKeySection;
